import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, AppWindow, Music, MessageSquare, Settings, LogOut,
  Plus, Pencil, Trash2, Save, X, ChevronRight, Wallet, Users, DollarSign, CheckCircle, XCircle, Clock, BarChart3, TrendingUp
} from "lucide-react";
import { api } from "@/lib/api";

const tabs = [
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "apps", label: "Apps", icon: AppWindow },
  { id: "media", label: "Media", icon: Music },
  { id: "contacts", label: "Contacts", icon: MessageSquare },
  { id: "payouts", label: "Payouts", icon: Wallet },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [token, setToken] = useState("");
  const [analytics, setAnalytics] = useState(null);

  // Data states
  const [apps, setApps] = useState([]);
  const [media, setMedia] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Form states
  const [showAppForm, setShowAppForm] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [appForm, setAppForm] = useState({ name: "", tagline: "", description: "", image_url: "", platforms: [], website_url: "", ios_link: "", android_link: "", category: "app", order: 0, is_featured: false });

  const [showMediaForm, setShowMediaForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaForm, setMediaForm] = useState({ title: "", media_type: "music", description: "", image_url: "", link: "", order: 0 });
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    const t = localStorage.getItem("fortis_token");
    if (!t) { navigate("/admin"); return; }
    setToken(t);
  }, [navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    try {
      const [appsData, mediaData, contactsData, payoutsData, analyticsData] = await Promise.all([
        api.getApps(),
        api.getMedia(),
        api.getContacts(token),
        api.getAdminPayouts(token),
        api.getAnalytics(token),
      ]);
      setApps(appsData);
      setMedia(mediaData);
      setContacts(contactsData);
      setPayouts(payoutsData);
      setAnalytics(analyticsData);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("fortis_token");
        navigate("/admin");
      }
    }
  }, [token, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = () => {
    localStorage.removeItem("fortis_token");
    localStorage.removeItem("fortis_email");
    navigate("/admin");
  };

  // App CRUD
  const handleSaveApp = async (e) => {
    e.preventDefault();
    try {
      if (editingApp) {
        await api.updateApp(editingApp.id, appForm, token);
      } else {
        await api.createApp(appForm, token);
      }
      setShowAppForm(false);
      setEditingApp(null);
      setAppForm({ name: "", tagline: "", description: "", image_url: "", platforms: [], website_url: "", category: "app", order: 0, is_featured: false });
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setAppForm({
      name: app.name, tagline: app.tagline, description: app.description,
      image_url: app.image_url, platforms: app.platforms || [],
      website_url: app.website_url || "", ios_link: app.ios_link || "",
      android_link: app.android_link || "", category: app.category || "app",
      order: app.order || 0, is_featured: app.is_featured || false,
    });
    setShowAppForm(true);
  };

  const handleDeleteApp = async (id) => {
    if (!window.confirm("Delete this app?")) return;
    await api.deleteApp(id, token);
    loadData();
  };

  // Media CRUD
  const handleSaveMedia = async (e) => {
    e.preventDefault();
    try {
      if (editingMedia) {
        await api.updateMedia(editingMedia.id, mediaForm, token);
      } else {
        await api.createMedia(mediaForm, token);
      }
      setShowMediaForm(false);
      setEditingMedia(null);
      setMediaForm({ title: "", media_type: "music", description: "", image_url: "", link: "", order: 0 });
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleEditMedia = (item) => {
    setEditingMedia(item);
    setMediaForm({
      title: item.title, media_type: item.media_type, description: item.description,
      image_url: item.image_url || "", link: item.link || "", order: item.order || 0,
    });
    setShowMediaForm(true);
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Delete this media?")) return;
    await api.deleteMedia(id, token);
    loadData();
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Delete this contact submission?")) return;
    await api.deleteContact(id, token);
    loadData();
  };

  const togglePlatform = (platform) => {
    setAppForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all text-sm";

  return (
    <div className="min-h-screen bg-[#05050A] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/10 bg-[#0F0F1A]/50 p-6 flex-col hidden md:flex">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#4D00FF] flex items-center justify-center text-black font-black text-lg font-['Unbounded']">
            F
          </div>
          <div>
            <p className="font-['Unbounded'] font-bold text-white text-sm">Control Room</p>
            <p className="text-[#A0A0AB] text-xs">Fortis Digital</p>
          </div>
        </div>

        <nav className="flex-1 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20"
                  : "text-[#A0A0AB] hover:text-white hover:bg-white/5"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {activeTab === tab.id && <ChevronRight className="w-4 h-4 ml-auto" />}
            </button>
          ))}
        </nav>

        <button
          data-testid="admin-logout"
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#A0A0AB] hover:text-red-400 hover:bg-red-400/10 transition-all text-sm"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </aside>

      {/* Mobile tabs */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0F0F1A] border-t border-white/10 flex">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`admin-mobile-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs ${
              activeTab === tab.id ? "text-[#00F0FF]" : "text-[#A0A0AB]"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        <button
          data-testid="admin-mobile-logout"
          onClick={handleLogout}
          className="flex-1 flex flex-col items-center gap-1 py-3 text-xs text-[#A0A0AB]"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>

      {/* Main content */}
      <main className="flex-1 p-6 sm:p-8 overflow-y-auto pb-24 md:pb-8">
        <div className="max-w-5xl mx-auto">

          {/* Analytics Tab */}
          {activeTab === "analytics" && analytics && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="mb-8">
                <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Analytics Dashboard</h1>
                <p className="text-[#A0A0AB] text-sm mt-1">Platform overview</p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Total Revenue", value: `$${analytics.total_revenue.toFixed(2)}`, icon: DollarSign, color: "#00F0FF" },
                  { label: "Commission Earned", value: `$${analytics.total_commission.toFixed(2)}`, icon: TrendingUp, color: "#FF007F" },
                  { label: "Creators", value: analytics.total_creators, icon: Users, color: "#4D00FF" },
                  { label: "Products", value: analytics.total_products, icon: AppWindow, color: "#00F0FF" },
                ].map((m) => (
                  <div key={m.label} className="glass-card rounded-2xl p-5">
                    <m.icon className="w-5 h-5 mb-2" style={{ color: m.color }} />
                    <p className="text-2xl font-bold text-white">{m.value}</p>
                    <p className="text-[#A0A0AB] text-xs">{m.label}</p>
                  </div>
                ))}
              </div>

              {/* Revenue Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="glass-card rounded-2xl p-5">
                  <p className="text-[#A0A0AB] text-xs mb-1">Product Sales</p>
                  <p className="text-xl font-bold text-white">${analytics.product_revenue.toFixed(2)}</p>
                </div>
                <div className="glass-card rounded-2xl p-5">
                  <p className="text-[#A0A0AB] text-xs mb-1">Tier Subscriptions</p>
                  <p className="text-xl font-bold text-white">${analytics.tier_revenue.toFixed(2)}</p>
                </div>
                <div className="glass-card rounded-2xl p-5">
                  <p className="text-[#A0A0AB] text-xs mb-1">Featured Listings</p>
                  <p className="text-xl font-bold text-white">${analytics.featured_revenue.toFixed(2)}</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-white font-semibold text-sm mb-4">Creator Tiers</h3>
                  <div className="space-y-3">
                    {Object.entries(analytics.tier_counts || {}).map(([tier, count]) => (
                      <div key={tier} className="flex items-center justify-between">
                        <span className="text-[#A0A0AB] text-sm capitalize">{tier}</span>
                        <span className="text-white font-bold">{count}</span>
                      </div>
                    ))}
                    {Object.keys(analytics.tier_counts || {}).length === 0 && <p className="text-[#A0A0AB] text-xs">No creators yet</p>}
                  </div>
                </div>
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-white font-semibold text-sm mb-4">Pending Payouts</h3>
                  <p className="text-3xl font-bold text-white">{analytics.pending_payouts}</p>
                  <p className="text-[#A0A0AB] text-xs">requests totaling ${analytics.pending_payout_amount.toFixed(2)}</p>
                  <div className="mt-4 flex gap-4 text-sm">
                    <span className="text-[#A0A0AB]">Total Sales: <span className="text-white font-bold">{analytics.total_sales}</span></span>
                    <span className="text-[#A0A0AB]">Contacts: <span className="text-white font-bold">{analytics.total_contacts}</span></span>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              {analytics.recent_transactions?.length > 0 && (
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-white font-semibold text-sm mb-4">Recent Transactions</h3>
                  <div className="space-y-3">
                    {analytics.recent_transactions.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white text-sm">{t.description || "Payment"}</p>
                          <p className="text-[#A0A0AB] text-[10px] font-['JetBrains_Mono']">{new Date(t.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className="text-[#00F0FF] font-bold">${t.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Apps Tab */}
          {activeTab === "apps" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Apps & Services</h1>
                  <p className="text-[#A0A0AB] text-sm mt-1">{apps.length} apps listed</p>
                </div>
                <button
                  data-testid="add-app-button"
                  onClick={() => { setShowAppForm(true); setEditingApp(null); setAppForm({ name: "", tagline: "", description: "", image_url: "", platforms: [], website_url: "", ios_link: "", android_link: "", category: "app", order: 0, is_featured: false }); }}
                  className="neon-btn flex items-center gap-2 text-sm py-2 px-5"
                >
                  <Plus className="w-4 h-4" /> Add App
                </button>
              </div>

              {/* App Form */}
              {showAppForm && (
                <div className="glass-card rounded-2xl p-6 mb-8" data-testid="app-form">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-['Unbounded'] text-lg font-semibold text-white">
                      {editingApp ? "Edit App" : "New App"}
                    </h2>
                    <button onClick={() => { setShowAppForm(false); setEditingApp(null); }} className="text-[#A0A0AB] hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleSaveApp} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Name</label>
                      <input data-testid="app-form-name" className={inputClass} value={appForm.name} onChange={(e) => setAppForm({...appForm, name: e.target.value})} required placeholder="App name" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Tagline</label>
                      <input data-testid="app-form-tagline" className={inputClass} value={appForm.tagline} onChange={(e) => setAppForm({...appForm, tagline: e.target.value})} placeholder="Short tagline" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Description</label>
                      <textarea data-testid="app-form-description" className={inputClass + " resize-none"} rows={3} value={appForm.description} onChange={(e) => setAppForm({...appForm, description: e.target.value})} placeholder="App description" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Image URL</label>
                      <input data-testid="app-form-image" className={inputClass} value={appForm.image_url} onChange={(e) => setAppForm({...appForm, image_url: e.target.value})} placeholder="https://..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Website URL</label>
                      <input data-testid="app-form-website" className={inputClass} value={appForm.website_url} onChange={(e) => setAppForm({...appForm, website_url: e.target.value})} placeholder="https://..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">iOS App Store Link</label>
                      <input data-testid="app-form-ios-link" className={inputClass} value={appForm.ios_link} onChange={(e) => setAppForm({...appForm, ios_link: e.target.value})} placeholder="https://apps.apple.com/..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Google Play Link</label>
                      <input data-testid="app-form-android-link" className={inputClass} value={appForm.android_link} onChange={(e) => setAppForm({...appForm, android_link: e.target.value})} placeholder="https://play.google.com/..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Category</label>
                      <select data-testid="app-form-category" className={inputClass} value={appForm.category} onChange={(e) => setAppForm({...appForm, category: e.target.value})}>
                        <option value="app">App</option>
                        <option value="health">Health</option>
                        <option value="wellness">Wellness</option>
                        <option value="media">Media</option>
                        <option value="productivity">Productivity</option>
                        <option value="gaming">Gaming</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Platforms</label>
                      <div className="flex gap-2 mt-1">
                        {["iOS", "Android", "Web"].map((p) => (
                          <button key={p} type="button" onClick={() => togglePlatform(p)}
                            className={`px-3 py-2 rounded-lg text-xs border transition-all ${appForm.platforms.includes(p) ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#A0A0AB]"}`}>
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-[#A0A0AB] cursor-pointer">
                        <input type="checkbox" checked={appForm.is_featured} onChange={(e) => setAppForm({...appForm, is_featured: e.target.checked})} className="accent-[#00F0FF]" />
                        Featured
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-[#A0A0AB]">Order:</label>
                        <input type="number" className={inputClass + " w-20"} value={appForm.order} onChange={(e) => setAppForm({...appForm, order: parseInt(e.target.value) || 0})} />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <button data-testid="app-form-submit" type="submit" className="neon-btn flex items-center gap-2 text-sm py-2 px-6">
                        <Save className="w-4 h-4" /> {editingApp ? "Update" : "Create"} App
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* App List */}
              <div className="space-y-4">
                {apps.map((app) => (
                  <div key={app.id} data-testid={`admin-app-${app.id}`} className="glass-card rounded-2xl p-5 flex items-center gap-5">
                    {app.image_url && (
                      <img src={app.image_url} alt={app.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm truncate">{app.name}</h3>
                      <p className="text-[#A0A0AB] text-xs truncate">{app.tagline}</p>
                      <div className="flex gap-1 mt-1">
                        {app.platforms?.map(p => (
                          <span key={p} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-[#A0A0AB]">{p}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`edit-app-${app.id}`} onClick={() => handleEditApp(app)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-all">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-app-${app.id}`} onClick={() => handleDeleteApp(app.id)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-red-400 hover:bg-red-400/10 transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Media Tab */}
          {activeTab === "media" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Media & Music</h1>
                  <p className="text-[#A0A0AB] text-sm mt-1">{media.length} items</p>
                </div>
                <button
                  data-testid="add-media-button"
                  onClick={() => { setShowMediaForm(true); setEditingMedia(null); setMediaForm({ title: "", media_type: "music", description: "", image_url: "", link: "", order: 0 }); }}
                  className="neon-btn flex items-center gap-2 text-sm py-2 px-5"
                >
                  <Plus className="w-4 h-4" /> Add Media
                </button>
              </div>

              {showMediaForm && (
                <div className="glass-card rounded-2xl p-6 mb-8" data-testid="media-form">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-['Unbounded'] text-lg font-semibold text-white">
                      {editingMedia ? "Edit Media" : "New Media"}
                    </h2>
                    <button onClick={() => { setShowMediaForm(false); setEditingMedia(null); }} className="text-[#A0A0AB] hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleSaveMedia} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Title</label>
                      <input data-testid="media-form-title" className={inputClass} value={mediaForm.title} onChange={(e) => setMediaForm({...mediaForm, title: e.target.value})} required placeholder="Title" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Type</label>
                      <select data-testid="media-form-type" className={inputClass} value={mediaForm.media_type} onChange={(e) => setMediaForm({...mediaForm, media_type: e.target.value})}>
                        <option value="music">Music</option>
                        <option value="video">Video</option>
                        <option value="image">Image</option>
                        <option value="podcast">Podcast</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Description</label>
                      <textarea data-testid="media-form-description" className={inputClass + " resize-none"} rows={3} value={mediaForm.description} onChange={(e) => setMediaForm({...mediaForm, description: e.target.value})} placeholder="Description" />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Image URL</label>
                      <input data-testid="media-form-image" className={inputClass} value={mediaForm.image_url} onChange={(e) => setMediaForm({...mediaForm, image_url: e.target.value})} placeholder="https://..." />
                    </div>
                    <div>
                      <label className="text-xs text-[#A0A0AB] mb-1 block">Link</label>
                      <input data-testid="media-form-link" className={inputClass} value={mediaForm.link} onChange={(e) => setMediaForm({...mediaForm, link: e.target.value})} placeholder="https://..." />
                    </div>
                    <div className="md:col-span-2">
                      <button data-testid="media-form-submit" type="submit" className="neon-btn flex items-center gap-2 text-sm py-2 px-6">
                        <Save className="w-4 h-4" /> {editingMedia ? "Update" : "Create"} Media
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="space-y-4">
                {media.length === 0 ? (
                  <div className="glass-card rounded-2xl p-8 text-center">
                    <Music className="w-8 h-8 text-[#FF007F] mx-auto mb-3" />
                    <p className="text-[#A0A0AB] text-sm">No media items yet. Add your first one!</p>
                  </div>
                ) : (
                  media.map((item) => (
                    <div key={item.id} data-testid={`admin-media-${item.id}`} className="glass-card rounded-2xl p-5 flex items-center gap-5">
                      {item.image_url && (
                        <img src={item.image_url} alt={item.title} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-semibold text-sm truncate">{item.title}</h3>
                        <p className="text-[#A0A0AB] text-xs capitalize">{item.media_type}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => handleEditMedia(item)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-[#00F0FF] transition-all">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteMedia(item.id)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-red-400 transition-all">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* Contacts Tab */}
          {activeTab === "contacts" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="mb-8">
                <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Contact Submissions</h1>
                <p className="text-[#A0A0AB] text-sm mt-1">{contacts.length} messages</p>
              </div>

              <div className="space-y-4">
                {contacts.length === 0 ? (
                  <div className="glass-card rounded-2xl p-8 text-center">
                    <MessageSquare className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" />
                    <p className="text-[#A0A0AB] text-sm">No contact submissions yet.</p>
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <div key={contact.id} data-testid={`admin-contact-${contact.id}`} className="glass-card rounded-2xl p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-semibold text-sm">{contact.name}</h3>
                            <span className="text-[#00F0FF] text-xs font-['JetBrains_Mono']">{contact.email}</span>
                          </div>
                          <p className="text-[#A0A0AB] text-sm leading-relaxed">{contact.message}</p>
                          <p className="text-[#A0A0AB]/50 text-xs mt-2 font-['JetBrains_Mono']">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteContact(contact.id)} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-[#A0A0AB] hover:text-red-400 shrink-0 transition-all">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* Payouts Tab */}
          {activeTab === "payouts" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="mb-8">
                <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Creator Payouts</h1>
                <p className="text-[#A0A0AB] text-sm mt-1">{payouts.filter(p => p.status === "pending").length} pending requests</p>
              </div>

              <div className="space-y-4">
                {payouts.length === 0 ? (
                  <div className="glass-card rounded-2xl p-8 text-center">
                    <Wallet className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" />
                    <p className="text-[#A0A0AB] text-sm">No payout requests yet.</p>
                  </div>
                ) : payouts.map((payout) => (
                  <div key={payout.id} data-testid={`admin-payout-${payout.id}`} className="glass-card rounded-2xl p-5">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        {payout.status === "pending" && <Clock className="w-6 h-6 text-yellow-400" />}
                        {payout.status === "approved" && <CheckCircle className="w-6 h-6 text-[#00F0FF]" />}
                        {payout.status === "rejected" && <XCircle className="w-6 h-6 text-red-400" />}
                        <div>
                          <p className="text-white font-bold">${payout.amount.toFixed(2)}</p>
                          <p className="text-[#A0A0AB] text-xs">{payout.creator_name} ({payout.creator_email})</p>
                          <p className="text-[#A0A0AB] text-[10px] font-['JetBrains_Mono'] mt-1">
                            {payout.payment_method} — {payout.payment_details || "No details"} — {new Date(payout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-3 py-1 rounded-full capitalize ${payout.status === "pending" ? "bg-yellow-400/10 text-yellow-400" : payout.status === "approved" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "bg-red-400/10 text-red-400"}`}>
                          {payout.status}
                        </span>
                        {payout.status === "pending" && (
                          <div className="flex gap-2">
                            <button
                              data-testid={`approve-payout-${payout.id}`}
                              onClick={async () => { await api.approvePayout(payout.id, token); loadData(); }}
                              className="px-3 py-1.5 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] text-xs font-medium hover:bg-[#00F0FF]/20 transition-all"
                            >Approve</button>
                            <button
                              data-testid={`reject-payout-${payout.id}`}
                              onClick={async () => { await api.rejectPayout(payout.id, token); loadData(); }}
                              className="px-3 py-1.5 rounded-lg bg-red-400/10 text-red-400 text-xs font-medium hover:bg-red-400/20 transition-all"
                            >Reject</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="mb-8">
                <h1 className="font-['Unbounded'] text-2xl font-bold text-white">Site Settings</h1>
                <p className="text-[#A0A0AB] text-sm mt-1">Manage your site configuration</p>
              </div>
              <div className="glass-card rounded-2xl p-8 text-center">
                <Settings className="w-8 h-8 text-[#00F0FF] mx-auto mb-3" />
                <p className="text-[#A0A0AB] text-sm">Site settings will be customizable here. Use the Apps and Media tabs to manage your content.</p>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
